import { useNavigate } from "react-router-dom";
import { Camera, Megaphone, TrendingUp, HeartHandshake, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { n: "01", icon: Camera, title: "Property Styling & Positioning", body: "We rebrand, restyle and re-photograph your property. New name, new identity, new visuals." },
  { n: "02", icon: Megaphone, title: "Social Media Management", body: "Daily Reels, paid ads, Google + Instagram presence — managed by our in-house content team." },
  { n: "03", icon: TrendingUp, title: "Lead Generation", body: "Wedding planners, corporate channels, organic SEO and our 12-person host desk feeding bookings 24/7." },
  { n: "04", icon: HeartHandshake, title: "Booking Operations", body: "Check-ins, vendors, decor, food, music, staff. We run the operation. You see only the revenue." },
];

const COLLAGE_LEFT = [
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=85&w=600",
  "https://images.pexels.com/photos/33575697/pexels-photo-33575697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&q=85&w=600",
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=85&w=600",
];
const COLLAGE_RIGHT = [
  "https://images.pexels.com/photos/34597472/pexels-photo-34597472.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&q=85&w=600",
  "https://images.unsplash.com/photo-1571508601891-ca5e7a713859?auto=format&fit=crop&q=85&w=600",
  "https://images.pexels.com/photos/31860145/pexels-photo-31860145.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=600",
];

export default function ManagedPitch() {
  const navigate = useNavigate();
  return (
    <section className="bg-[#0E2E24] text-[#FAFAF8] py-24 md:py-28" data-testid="managed-pitch-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* LEFT — vertical scrolling collage */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative grid grid-cols-2 gap-3 h-[520px] overflow-hidden rounded-2xl">
            {/* solid fade bands (no gradient) */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-12 z-10 bg-[#0E2E24]" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 z-10 bg-[#0E2E24]" />

            <div className="overflow-hidden">
              <div className="scroll-y flex flex-col gap-3">
                {[...COLLAGE_LEFT, ...COLLAGE_LEFT].map((src, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden aspect-[4/5]">
                    <img src={src} alt="" className="img-cover" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
            <div className="overflow-hidden">
              <div className="scroll-y-reverse flex flex-col gap-3">
                {[...COLLAGE_RIGHT, ...COLLAGE_RIGHT].map((src, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden aspect-[4/5]">
                    <img src={src} alt="" className="img-cover" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — copy + 4 process cards + CTA */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="kicker text-[#F4A930] mb-4">Appy Managed</div>
          <h2 className="font-sora text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            We don't just list farmhouses.
            <br />
            <span className="text-[#F4A930]">We build booking brands.</span>
          </h2>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className="bg-[#163b2f] border border-[#FAFAF8]/8 rounded-2xl p-6 hover:border-[#F4A930]/60 transition-colors"
                data-testid={`managed-step-${i + 1}`}
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-xl bg-[#F4A930]/15 flex items-center justify-center">
                    <s.icon size={18} className="text-[#F4A930]" />
                  </div>
                  <span className="font-sora font-semibold text-lg text-[#F4A930]/50">{s.n}</span>
                </div>
                <h3 className="mt-5 font-sora text-base font-semibold text-[#FAFAF8] leading-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-[#FAFAF8]/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <Button
            onClick={() => navigate("/partner")}
            className="mt-10 bg-[#F4A930] hover:bg-[#d99825] text-[#0E2E24] rounded-full px-7 py-6 font-sora font-semibold gap-2"
            data-testid="managed-partner-cta"
          >
            Partner with Appy Stays <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </section>
  );
}
